import { useEffect, useState } from 'react'

import { useQuery } from '@api/hooks'
import { Purchase as PurchaseRecord } from '@domain'
import { Divider, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import ContractLoadingApi, { ContractLoadingKeys } from '@services/contractLoading.api'
import {
	ContractSummary,
	ContractSummaryMatchTypeFilter,
	ContractSummaryPurchaseCounts,
	ContractSummaryStatus,
} from '@services/contractLoading.types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PurchaseCard } from '@views/MarketBaskets/ActionCenter/Components/PurchaseCard/PurchaseCard'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'

import {
	addToTargetList,
	removeFromPendingList,
	restoreCachesFromSnapshot,
	snapshotContractCaches,
	updateAllList,
	updateDetailContractStatus,
	updateSummaryCache,
} from '../contractLoading.utils'
import { Summary } from './Summary'

type Props = {
	contractId: Id
	onUpdateContract: (id: Id) => void
}

export default function Contract({ contractId, onUpdateContract }: Props) {
	const [activeTab, setActiveTab] = useState<ContractSummaryMatchTypeFilter>('ALL')

	const { marketBasket } = useMarketBasketContext()
	const currency = marketBasket?.currency
	const queryClient = useQueryClient()
	const { marketBasketId } = useParams()

	useEffect(() => {
		setActiveTab('ALL')
	}, [contractId])

	const { data: summaryData } = useQuery({
		queryKey: ContractLoadingKeys.contract(marketBasketId, contractId, { matchType: 'ALL' }),
		queryFn: () => ContractLoadingApi.contract(contractId, { matchType: 'ALL' }),
	})

	const { data, isLoading } = useQuery({
		queryKey: ContractLoadingKeys.contract(marketBasketId, contractId, { matchType: activeTab }),
		queryFn: () => ContractLoadingApi.contract(contractId, { matchType: activeTab }),
	})

	const handleUpdateContract = async (
		status: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED
	) => {
		await queryClient.cancelQueries({ queryKey: ContractLoadingKeys.all(marketBasketId) })

		// Snapshot current cache state
		const snapshot = snapshotContractCaches(
			queryClient,
			marketBasketId,
			contractId,
			status,
			activeTab
		)

		// total savings to be added to summary if accepted
		const annualTotalSavings = snapshot.updatedContractData?.summary.annualTotalSavings ?? 0

		// Apply optimistic updates
		updateSummaryCache(queryClient, marketBasketId, status, annualTotalSavings)
		removeFromPendingList(queryClient, marketBasketId, contractId)
		addToTargetList(queryClient, marketBasketId, contractId, status, snapshot.updatedContractData)
		updateAllList(queryClient, marketBasketId, contractId, status, snapshot.updatedContractData)
		updateDetailContractStatus(
			queryClient,
			marketBasketId,
			contractId,
			status,
			snapshot.updatedContractData,
			activeTab
		)

		onUpdateContract(null)

		return {
			...snapshot,
			status,
		}
	}

	const handleUpdateError = (
		_err: unknown,
		_vars: unknown,
		context:
			| {
					previousSummaryData: unknown
					previousPendingListData: ContractSummary[] | undefined
					previousTargetListData: ContractSummary[] | undefined
					updatedContractData: unknown
					status: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED
					// formatter is having issues with the indentation here
					// eslint-disable-next-line no-mixed-spaces-and-tabs
			  }
			| undefined
	) => {
		if (!context) return
		restoreCachesFromSnapshot(queryClient, context as any, marketBasketId)
	}

	const handleUpdateSettled = () => {
		queryClient.invalidateQueries({
			queryKey: ContractLoadingKeys.all(marketBasketId),
		})
	}

	const { mutate: acceptContract } = useMutation({
		mutationFn: ContractLoadingApi.accept,
		onMutate: () => handleUpdateContract(ContractSummaryStatus.ACCEPTED),
		onError: handleUpdateError,
		onSettled: handleUpdateSettled,
	})

	const { mutate: rejectContract } = useMutation({
		mutationFn: ContractLoadingApi.reject,
		onMutate: () => handleUpdateContract(ContractSummaryStatus.REJECTED),
		onError: handleUpdateError,
		onSettled: handleUpdateSettled,
	})

	return (
		<Stack sx={{ width: '100%', height: '100%', overflow: 'hidden', p: 2 }}>
			<Typography variant="h6" mb={2}>
				Selected Price Contract
			</Typography>

			<Paper
				elevation={2}
				sx={{
					borderRadius: 2,
					flex: 1,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Summary
					summary={summaryData?.summary}
					currency={currency}
					onAccept={() => acceptContract(contractId)}
					onReject={() => rejectContract(contractId)}
				/>

				<Divider />

				<PurchaseListUI
					purchases={data?.purchases ?? []}
					purchaseCounts={summaryData?.purchaseCounts}
					isLoading={isLoading}
					currency={currency}
					activeTab={activeTab}
					onChangeActiveTab={setActiveTab}
				/>
			</Paper>
		</Stack>
	)
}

type PurchaseListUIProps = {
	purchases: PurchaseRecord[]
	purchaseCounts: ContractSummaryPurchaseCounts | undefined
	isLoading: boolean
	currency: string
	activeTab: ContractSummaryMatchTypeFilter
	onChangeActiveTab: (activeTab: ContractSummaryMatchTypeFilter) => void
}

export function PurchaseListUI({
	purchases,
	purchaseCounts,
	isLoading,
	currency,
	activeTab,
	onChangeActiveTab,
}: PurchaseListUIProps) {
	return (
		<Stack
			sx={{ flex: 1, overflow: 'hidden', px: 2, pt: 2 }}
			data-testid="contract-loading-detail-purchase-list"
		>
			<Typography variant="h6">Switch Suggestions</Typography>
			<Tabs
				value={activeTab}
				onChange={(_, value) => {
					onChangeActiveTab(value)
				}}
			>
				<Tab label={`All (${purchaseCounts?.total ?? '--'})`} value="ALL" />
				<Tab label={`Exact Matches (${purchaseCounts?.exactMatches ?? '--'})`} value="EXACT" />
				<Tab
					label={`Similar Matches (${purchaseCounts?.similarMatches ?? '--'})`}
					value="SIMILAR"
				/>
			</Tabs>

			<Stack spacing={2} sx={{ overflowY: 'auto', flex: 1, mt: 2, pb: 2 }}>
				{isLoading && (
					<>
						<Skeleton variant="rectangular" width="100%" height={72} />
						<Skeleton variant="rectangular" width="100%" height={72} />
						<Skeleton variant="rectangular" width="100%" height={72} />
					</>
				)}

				{!isLoading && purchases.length === 0 && (
					<Stack flex={1} alignItems="center" justifyContent="center">
						<Typography variant="h6" color="text.secondary">
							No purchases found
						</Typography>
					</Stack>
				)}

				{!isLoading &&
					purchases.map((purchase) => (
						<PurchaseCard key={purchase.id} purchase={purchase} currency={currency} />
					))}
			</Stack>
		</Stack>
	)
}
